import { Box, Tooltip, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRowSelectionModel,
  GridSortModel,
  DataGrid,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { JobRowData } from '@/api/definition'
import { fetchPaginatedJobs, postCancelJobs } from '@/api/fota'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import { DownloadReadyState } from '@/pages/fota/jobState'
import {
  DateTime,
  FotaJobStateFormatter,
  IsOnline,
  Uid,
} from '@/pages/utils/formatter'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import { useTableUrlState } from '@/hooks/useTableUrlState'

type Props = {
  setActiveStep: (step: number) => void
  selectedRows: number[]
  onSelectedJobs: (selectedRows: number[]) => void
  enableFilters?: boolean
  enableSearch?: boolean
}

const SelectDownloadReadyJobs = ({
  setActiveStep,
  selectedRows,
  onSelectedJobs,
  enableFilters = false,
  enableSearch = true,
}: Props) => {
  const { t } = useTranslation(['di', 'app'])
  const { enqueueSnackbar } = useSnackbar()

  // URL state hook
  const { urlState, updateUrlState } = useTableUrlState()

  // State management - use URL state
  const [data, setData] = useState<JobRowData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [rowCount, setRowCount] = useState<number>(0)
  const [open, setOpen] = useState(false)
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false)

  // Local search state
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.searchTerm)
  const searchDebounceTimer = useRef<NodeJS.Timeout>()

  // Refs
  const mountedRef = useRef(false)
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Use URL state for these values
  const page = urlState.pageIndex
  const pageSize = urlState.pageSize
  const searchTerm = urlState.searchTerm
  const sortModel = urlState.sortModel

  const handleConfirm = useCallback(() => {
    setOpen(false)
    setActiveStep(1)
  }, [setActiveStep])

  const handleCancel = useCallback(() => {
    setOpen(false)
  }, [])

  const fetchData = useCallback(
    async (isAutoRefresh = false) => {
      if (!mountedRef.current) return

      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      if (!isAutoRefresh) setLoading(true)

      try {
        let sortParam = 'updated_at'
        if (sortModel.length > 0) {
          sortParam =
            sortModel[0].field === 'state' ? 'State' : sortModel[0].field
        }
        const sortDirection = sortModel.length > 0 ? sortModel[0].sort : 'desc'
        const state = [DownloadReadyState]

        const { data: jobData, totalRows } = await fetchPaginatedJobs({
          page,
          pageSize,
          sortBy: sortParam,
          sortDir: sortDirection,
          searchTerm: searchTerm.trim(),
          state,
        })

        const canUpdate =
          mountedRef.current && !abortControllerRef.current?.signal.aborted

        if (canUpdate) {
          setData(jobData)
          setRowCount(totalRows)
        }
      } catch (error) {
        const canShowError =
          mountedRef.current && !abortControllerRef.current?.signal.aborted
        if (canShowError) {
          console.error('Error fetching data:', error)
          enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
        }
      } finally {
        if (mountedRef.current && !isAutoRefresh) {
          setLoading(false)
        }
      }
    },
    [page, pageSize, sortModel, searchTerm, enqueueSnackbar, t],
  )

  const handleCancelJob = useCallback(async () => {
    try {
      const result = await postCancelJobs(selectedRows.map(Number))
      const failCount = result.filter((i) => !i.successful).length
      const passCount = result.filter((i) => i.successful).length

      if (failCount > 0 && passCount > 0) {
        enqueueSnackbar(
          t('di:fota.fotaCancelJobsToastsMessages.partiallyCompleted', {
            failCount: failCount.toString(),
            passCount: passCount.toString(),
          }),
          { variant: 'warning' },
        )
      } else if (failCount === 0 && passCount > 0) {
        enqueueSnackbar(t('di:fota.fotaCancelJobsToastsMessages.success'), {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(t('di:fota.fotaCancelJobsToastsMessages.failed'), {
          variant: 'error',
        })
      }

      fetchData()
      setOpenCancelDialog(false)
    } catch (error) {
      console.error('Error canceling jobs:', error)
      enqueueSnackbar(t('common:request.serverError'), { variant: 'error' })
    }
  }, [selectedRows, enqueueSnackbar, t, fetchData])

  // Single effect that watches URL state
  useEffect(() => {
    mountedRef.current = true
    fetchData()

    return () => {
      mountedRef.current = false
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchData])

  // Auto-refresh effect
  useEffect(() => {
    if (mountedRef.current) {
      autoRefreshIntervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          fetchData(true)
        }
      }, 10000)

      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current)
          autoRefreshIntervalRef.current = null
        }
      }
    }
  }, [fetchData])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  const handleSelectionChange = useCallback(
    (ids: GridRowSelectionModel) => {
      onSelectedJobs(ids as number[])
    },
    [onSelectedJobs],
  )

  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      updateUrlState({
        pageIndex: params.page,
        pageSize: params.pageSize,
      })
    },
    [updateUrlState],
  )

  // Debounced search handler
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL state update (300ms)
      searchDebounceTimer.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 300)
    },
    [updateUrlState],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      updateUrlState({
        pageIndex: 0,
        sortModel: model,
      })
    },
    [updateUrlState],
  )

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <Uid value={params.value} />,
        width: 400,
        flex: 0,
        minWidth: 400,
        maxWidth: 400,
        resizable: false,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'is_online',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <IsOnline value={params.value} />,
        flex: 1,
        minWidth: 80,
      },
      {
        field: 'created_by',
        headerName: t('di:fota.createdBy'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'version',
        headerName: t('di:fota.firmwareVersion'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 80,
      },
      {
        field: 'state',
        headerName: t('di:fota.state'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <FotaJobStateFormatter state={params.value} />,
        flex: 1,
        minWidth: 80,
      },
      {
        field: 'updated_at',
        headerName: t('di:fota.lastChange'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        renderCell: (params) => <DateTime value={params.value} />,
        minWidth: 100,
      },
    ],
    [t],
  )

  const shouldShowCustomToolbar = enableFilters || enableSearch

  return (
    <Box
      sx={{
        borderRadius: '4px',
        overflow: 'auto',
        width: '100%',
        '& .MuiDataGrid-cell': {
          whiteSpace: 'normal',
          lineHeight: '1.5',
        },
      }}
    >
      <DataGrid
        data-testid="loading-state"
        data-loading={loading}
        rows={data}
        columns={columns}
        autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
        disableVirtualization
        loading={loading}
        checkboxSelection
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        rowCount={rowCount}
        pageSizeOptions={[5, 10, 25, 50]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={selectedRows}
        slots={shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined}
        slotProps={{
          toolbar: shouldShowCustomToolbar
            ? ({
                enableSearch: enableSearch,
                searchValue: localSearchTerm,
                onSearchChange: handleSearchChange,
              } as CustomToolbarProps)
            : undefined,
          pagination: {
            showFirstButton: true,
            showLastButton: true,
            ActionsComponent: TablePaginationActions,
          },
        }}
        isRowSelectable={(params) => params.row.is_online}
        disableColumnFilter={true}
        disableColumnMenu={true}
        disableDensitySelector={true}
        disableColumnSelector={true}
        localeText={{ noRowsLabel: 'No records found' }}
      />

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Tooltip title={t('device.fotaJobs.update')}>
          <span>
            <Button
              variant="contained"
              color="success"
              disabled={selectedRows.length === 0}
              onClick={() => setOpen(true)}
            >
              {t('device.fotaJobs.update')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t('device.fotaJobs.cancel')}>
          <span>
            <Button
              variant="contained"
              color="warning"
              disabled={selectedRows.length === 0 || loading}
              onClick={() => setOpenCancelDialog(true)}
              sx={{
                backgroundColor: '#ff9800',
                '&.Mui-disabled': {
                  opacity: 0.65,
                  backgroundColor: '#ff9800',
                  color: '#fff',
                },
              }}
            >
              {t('device.fotaJobs.cancel')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <ConfirmDialog
        open={open}
        setOpen={setOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        data-testid="update-dialog"
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.confirmFotaUpdateReady')}
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        open={openCancelDialog}
        setOpen={setOpenCancelDialog}
        onConfirm={handleCancelJob}
        onCancel={handleCancel}
        data-testid="cancel-dialog"
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.confirmCancelFailedJobs')}
        </Typography>
      </ConfirmDialog>
    </Box>
  )
}

export default React.memo(SelectDownloadReadyJobs)
